import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Generate a PDF for a quotation with Nonosky branding
 * @param {Object} quotation - The quotation data
 * @param {Array} details - Array of quotation details
 * @param {Array} images - Array of image URLs to include
 */
// Export individual functions for use in email functionality
export { addCoverPage, addHeader, addQuotationInfo, addClientInfo, addDetailsTable, addImages, addFooter };

export async function generateQuotationPDF(quotation, details = [], images = []) {
    try {
        // Validate inputs
        if (!quotation) {
            throw new Error('No quotation data provided');
        }

        // Create new PDF document
        const doc = new jsPDF('p', 'mm', 'a4');
        
        // Set up colors (Nonosky palette: black background with red accents)
        const colors = {
            primary: '#DC2626', // Red-600
            secondary: '#7F1D1D', // Red-800
            background: '#000000', // Black
            text: '#FFFFFF', // White
            textSecondary: '#D1D5DB' // Gray-300
        };

        // Add cover page first
        await addCoverPage(doc, colors);
        
        // Add new page for content
        doc.addPage();

        // Add background
        doc.setFillColor(colors.background);
        doc.rect(0, 0, 210, 297, 'F');

        // Add header with company branding
        await addHeader(doc, colors);
        
        // Add quotation information
        addQuotationInfo(doc, quotation, colors, details);
        
        // Add client information
        addClientInfo(doc, quotation, colors);
        
        // Add details table
        addDetailsTable(doc, details, colors);
        
        // Add images if available (adjust position to avoid footer overlap)
        console.log('🖼️ PDF Generator - Images received:', images);
        console.log('🖼️ PDF Generator - Images length:', images ? images.length : 'null');
        if (images && images.length > 0) {
            console.log('🖼️ PDF Generator - Calling addImages with', images.length, 'images');
            await addImages(doc, images, colors);
        } else {
            console.log('⚠️ PDF Generator - No images to add');
        }
        
        // Add footer
        addFooter(doc, colors);
        
        // Save the PDF
        const fileName = `Cotizacion_${quotation.numero || 'N/A'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
}

/**
 * Add cover pages with images from /covers/ folder
 * First page: 0.jpg
 * Second page: 1.jpg
 */
async function addCoverPage(doc, colors) {
    try {
        console.log('🖼️ Starting to add cover pages...');
        
        // Load the first cover image (0.jpg)
        console.log('🖼️ Loading first cover image: /covers/0.jpg');
        const coverImageData0 = await loadImageAsBase64('/covers/0.jpg');
        
        if (coverImageData0 && coverImageData0.length > 100) {
            // Add the first image to cover the entire first page
            // A4 dimensions: 210mm x 297mm
            try {
                doc.addImage(coverImageData0, 'JPEG', 0, 0, 210, 297);
                console.log('✅ First cover page (0.jpg) added successfully');
            } catch (imgError) {
                console.error('❌ Error adding first cover image to PDF:', imgError);
                addFallbackCoverPage(doc, colors);
            }
        } else {
            // Fallback: create a simple cover page
            console.warn('⚠️ Could not load first cover image, creating fallback cover');
            console.warn('⚠️ Image data:', coverImageData0 ? `Received ${coverImageData0.length} chars` : 'null');
            addFallbackCoverPage(doc, colors);
        }
        
        // Add new page for second cover
        doc.addPage();
        console.log('📄 Added new page for second cover');
        
        // Load the second cover image (1.jpg)
        console.log('🖼️ Loading second cover image: /covers/1.jpg');
        const coverImageData1 = await loadImageAsBase64('/covers/1.jpg');
        
        if (coverImageData1 && coverImageData1.length > 100) {
            // Add the second image to cover the entire second page
            try {
                doc.addImage(coverImageData1, 'JPEG', 0, 0, 210, 297);
                console.log('✅ Second cover page (1.jpg) added successfully');
            } catch (imgError) {
                console.error('❌ Error adding second cover image to PDF:', imgError);
                addFallbackCoverPage(doc, colors);
            }
        } else {
            // Fallback: create a simple cover page for second page
            console.warn('⚠️ Could not load second cover image, creating fallback cover');
            console.warn('⚠️ Image data:', coverImageData1 ? `Received ${coverImageData1.length} chars` : 'null');
            addFallbackCoverPage(doc, colors);
        }
        
        console.log('✅ Cover pages process completed');
    } catch (error) {
        console.error('❌ Error adding cover pages, using fallback:', error);
        console.error('❌ Error stack:', error.stack);
        try {
            addFallbackCoverPage(doc, colors);
            // Add second fallback page
            doc.addPage();
            addFallbackCoverPage(doc, colors);
        } catch (fallbackError) {
            console.error('❌ Error creating fallback cover pages:', fallbackError);
        }
    }
}

/**
 * Add fallback cover page if image loading fails
 */
function addFallbackCoverPage(doc, colors) {
    // Set background to black
    doc.setFillColor(colors.background);
    doc.rect(0, 0, 210, 297, 'F');
    
    // Add title
    doc.setTextColor(colors.text);
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.text('COTIZACIÓN DE SERVIDORES', 105, 150, { align: 'center' });
    
    // Add company name
    doc.setFontSize(24);
    doc.text('NONOSKY', 105, 180, { align: 'center' });
    
    // Add contact info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('PBX 2354-2000', 105, 220, { align: 'center' });
    doc.text('ventas@nonosky.com', 105, 235, { align: 'center' });
    doc.text('nonosky.com', 105, 250, { align: 'center' });
}

/**
 * Add header with company branding
 */
async function addHeader(doc, colors) {
    try {
        // Load the SVG logo and convert to canvas for jsPDF compatibility
        const logoData = await loadImageAsBase64('/assets/images/logo_nonosky.svg');
        
        if (logoData) {
            // Create canvas to convert SVG to image data
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            return new Promise((resolve, reject) => {
                img.onload = function() {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    
                    // Convert canvas to data URL
                    const pngData = canvas.toDataURL('image/png');
                    
                    // Add the converted image to PDF with correct aspect ratio
                    // Keep original aspect ratio: width/height ratio should be maintained
                    const logoWidth = 25; // Reduce width to maintain aspect ratio
                    const logoHeight = 25; // Keep height the same
                    doc.addImage(pngData, 'PNG', 15, 10, logoWidth, logoHeight);
                    
                    // Add company name and tagline
                    addCompanyInfo(doc, colors);
                    resolve();
                };
                
                img.onerror = function() {
                    console.warn('Failed to load logo image, using fallback');
                    addFallbackLogo(doc, colors);
                    resolve();
                };
                
                img.src = logoData;
            });
        } else {
            throw new Error('Logo data not available');
        }
    } catch (error) {
        console.warn('Could not load logo, using fallback:', error);
        addFallbackLogo(doc, colors);
    }
}

function addFallbackLogo(doc, colors) {
    // Fallback: Add a styled logo placeholder with Nonosky branding
    doc.setFillColor(colors.primary);
    // Use square dimensions to maintain aspect ratio
    doc.rect(15, 10, 25, 25, 'F');
    
    // Add "NS" text as fallback (representing "Nonosky")
    doc.setTextColor(colors.text);
    doc.setFontSize(14); // Slightly smaller to fit better
    doc.setFont('helvetica', 'bold');
    doc.text('NS', 23, 22);
    
    // Add a small decorative element
    doc.setFillColor(colors.text);
    doc.circle(26, 18, 2, 'F');
    
    // Add company info
    addCompanyInfo(doc, colors);
}

function addCompanyInfo(doc, colors) {
    // Company name (positioned next to logo)
    doc.setTextColor(colors.text);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('NONOSKY', 50, 25);
    
    // Tagline
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistema de Gestión de Cotizaciones', 50, 30);
    
    // Add a decorative line
    doc.setDrawColor(colors.primary);
    doc.setLineWidth(0.5);
    doc.line(10, 40, 200, 40);
}

/**
 * Add quotation information
 */
function addQuotationInfo(doc, quotation, colors, details = []) {
    const startY = 50; // Adjusted for new header layout
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.primary);
    doc.text('COTIZACIÓN', 15, startY);
    
    // Quotation details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colors.text);
    
    // Calculate total from details if quotation total is 0
    let displayTotal = quotation.total || 0;
    if (displayTotal === 0 && details && details.length > 0) {
        displayTotal = details.reduce((sum, detail) => sum + (detail.total || 0), 0);
    }
    
    const detailsInfo = [
        ['Número:', quotation.numero || 'N/A'],
        ['Fecha:', quotation.fecha ? new Date(quotation.fecha).toLocaleDateString() : 'N/A'],
        ['Total:', `$${displayTotal.toLocaleString()}`]
    ];
    
    let currentY = startY + 10;
    detailsInfo.forEach(([label, value]) => {
        doc.text(label, 15, currentY);
        doc.setFont('helvetica', 'bold');
        doc.text(value, 60, currentY);
        doc.setFont('helvetica', 'normal');
        currentY += 6;
    });
}

/**
 * Add client information
 */
function addClientInfo(doc, quotation, colors) {
    const startY = 90; // Adjusted for new header layout
    
    // Section title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.primary);
    doc.text('INFORMACIÓN DEL CLIENTE', 15, startY);
    
    // Client details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colors.text);
    
    const clientInfo = [
        ['Nombre:', quotation.nombre || 'N/A'],
        ['NIT:', quotation.nit || 'N/A'],
        ['Teléfono:', quotation.telefono || 'N/A'],
        ['Email:', quotation.correo || 'N/A']
    ];
    
    let currentY = startY + 10;
    clientInfo.forEach(([label, value]) => {
        doc.text(label, 15, currentY);
        doc.text(value, 60, currentY);
        currentY += 6;
    });
}

/**
 * Add details table
 */
function addDetailsTable(doc, details, colors) {
    const startY = 130; // Adjusted for new header layout
    
    // Section title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.primary);
    doc.text('DETALLES DE LA COTIZACIÓN', 15, startY);
    
    if (details.length === 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colors.textSecondary);
        doc.text('No hay detalles disponibles', 15, startY + 10);
        return;
    }
    
    // Prepare table data
    const tableData = details.map(detail => [
        detail.expand?.producto?.nombre || 'Producto',
        detail.cantidad || 0,
        `$${(detail.precio || 0).toLocaleString()}`,
        `$${(detail.total || 0).toLocaleString()}`
    ]);
    
    // Create table
    doc.autoTable({
        startY: startY + 10,
        head: [['Producto', 'Cantidad', 'Precio Unit.', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: {
            fillColor: colors.secondary,
            textColor: colors.text,
            fontStyle: 'bold'
        },
        bodyStyles: {
            fillColor: [20, 20, 20],
            textColor: colors.text,
            fontSize: 9
        },
        alternateRowStyles: {
            fillColor: [40, 40, 40]
        },
        styles: {
            lineColor: colors.primary,
            lineWidth: 0.1
        }
    });
}

/**
 * Add images to the PDF
 */
async function addImages(doc, images, colors) {
    console.log('🖼️ addImages function called with', images.length, 'images');
    console.log('🖼️ Image URLs:', images);
    
    const pageHeight = doc.internal.pageSize.height;
    const footerSpace = 40; // Space reserved for footer
    const maxY = pageHeight - footerSpace;
    
    const startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 200;
    
    // Check if we have enough space for images
    if (startY > maxY - 50) {
        // Not enough space, add to new page
        doc.addPage();
        const newStartY = 30;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.primary);
        doc.text('IMÁGENES DE REFERENCIA', 15, newStartY);
        return;
    }
    
    // Section title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.primary);
    doc.text('IMÁGENES DE REFERENCIA', 15, startY);
    
    if (!images || images.length === 0) {
        // Show message when no images are available
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colors.textSecondary);
        doc.text('No hay imágenes disponibles', 15, startY + 10);
        return;
    }
    
    let currentY = startY + 10;
    const imageWidth = 60;
    const imageHeight = 45;
    const imagesPerRow = 3;
    const spacing = 10;
    
    for (let i = 0; i < images.length; i++) {
        const imageUrl = images[i];
        const row = Math.floor(i / imagesPerRow);
        const col = i % imagesPerRow;
        
        const x = 15 + col * (imageWidth + spacing);
        const y = currentY + row * (imageHeight + spacing);
        
        // Check if image would overflow into footer area
        if (y + imageHeight > maxY) {
            // Add new page for remaining images
            doc.addPage();
            const newCurrentY = 30;
            const newRow = 0;
            const newY = newCurrentY + newRow * (imageHeight + spacing);
            
            // Continue with remaining images on new page
            if (i < images.length - 1) {
                // Add remaining images starting from new page
                for (let j = i; j < images.length; j++) {
                    const remainingImageUrl = images[j];
                    const remainingRow = Math.floor((j - i) / imagesPerRow);
                    const remainingCol = (j - i) % imagesPerRow;
                    
                    const remainingX = 15 + remainingCol * (imageWidth + spacing);
                    const remainingY = newCurrentY + remainingRow * (imageHeight + spacing);
                    
                    // Actually load the remaining images
                    try {
                        console.log(`🖼️ Loading remaining image ${j + 1}: ${remainingImageUrl}`);
                        const remainingImageData = await loadImageAsBase64(remainingImageUrl);
                        
                        if (remainingImageData && remainingImageData.length > 100) {
                            console.log(`✅ Remaining image ${j + 1} loaded successfully, size: ${remainingImageData.length} chars`);
                            
                            // Determine image format
                            let imageFormat = 'JPEG';
                            if (remainingImageData.includes('data:image/png')) {
                                imageFormat = 'PNG';
                            } else if (remainingImageData.includes('data:image/jpeg') || remainingImageData.includes('data:image/jpg')) {
                                imageFormat = 'JPEG';
                            }
                            
                            // Add the actual image
                            doc.addImage(remainingImageData, imageFormat, remainingX, remainingY, imageWidth, imageHeight);
                            
                            // Add image label
                            doc.setFontSize(8);
                            doc.setTextColor(colors.text);
                            const labelY = remainingY + imageHeight + 5;
                            if (labelY < doc.internal.pageSize.height - 10) {
                                doc.text(`Imagen ${j + 1}`, remainingX, labelY);
                            }
                        } else {
                            console.log(`❌ Remaining image ${j + 1} failed to load, using placeholder`);
                            // Fallback placeholder
                            doc.setFillColor(colors.secondary);
                            doc.rect(remainingX, remainingY, imageWidth, imageHeight, 'F');
                            doc.setTextColor(colors.text);
                            doc.setFontSize(8);
                            doc.text(`Imagen ${j + 1}`, remainingX + 5, remainingY + 25);
                        }
                    } catch (error) {
                        console.error(`❌ Error loading remaining image ${j + 1}:`, error);
                        // Fallback placeholder
                        doc.setFillColor(colors.secondary);
                        doc.rect(remainingX, remainingY, imageWidth, imageHeight, 'F');
                        doc.setTextColor(colors.text);
                        doc.setFontSize(8);
                        doc.text(`Imagen ${j + 1}`, remainingX + 5, remainingY + 25);
                    }
                }
            }
            break;
        }
        
        try {
            console.log(`🖼️ Attempting to load image ${i + 1}: ${imageUrl}`);
            
            // Try to load and add the actual image
            const imageData = await loadImageAsBase64(imageUrl);
            
            if (imageData && imageData.length > 100) { // Ensure we have actual data
                console.log(`✅ Image ${i + 1} loaded successfully, size: ${imageData.length} chars`);
                
                // Determine image format from the data URL
                let imageFormat = 'JPEG'; // default
                if (imageData.includes('data:image/png')) {
                    imageFormat = 'PNG';
                } else if (imageData.includes('data:image/jpeg') || imageData.includes('data:image/jpg')) {
                    imageFormat = 'JPEG';
                }
                
                console.log(`📄 Adding image ${i + 1} to PDF as ${imageFormat}`);
                
                // Add the actual image
                doc.addImage(imageData, imageFormat, x, y, imageWidth, imageHeight);
                
                // Add image label
                doc.setFontSize(8);
                doc.setTextColor(colors.text);
                const labelY = y + imageHeight + 5;
                if (labelY < doc.internal.pageSize.height - 10) {
                    doc.text(`Imagen ${i + 1}`, x, labelY);
                }
                
                console.log(`✅ Image ${i + 1} added to PDF successfully`);
            } else {
                console.log(`❌ Image ${i + 1} failed to load, using placeholder`);
                
                // Fallback: Add image placeholder with better styling
                doc.setFillColor('#F3F4F6'); // Light gray background
                doc.rect(x, y, imageWidth, imageHeight, 'F');
                
                // Add border
                doc.setDrawColor('#D1D5DB');
                doc.setLineWidth(1);
                doc.rect(x, y, imageWidth, imageHeight, 'S');
                
                // Add image icon (simple rectangle)
                const iconSize = Math.min(imageWidth, imageHeight) * 0.3;
                const iconX = x + (imageWidth - iconSize) / 2;
                const iconY = y + (imageHeight - iconSize) / 2 - 5;
                
                doc.setFillColor('#9CA3AF');
                doc.rect(iconX, iconY, iconSize, iconSize, 'F');
                
                // Add placeholder text
                doc.setFontSize(8);
                doc.setTextColor('#6B7280');
                doc.setFont('helvetica', 'normal');
                const textX = x + imageWidth/2;
                const textY = y + imageHeight - 8;
                doc.text('Imagen del producto', textX, textY, { align: 'center' });
                
                // Add image label
                doc.setFontSize(7);
                doc.setTextColor(colors.text);
                const labelY = y + imageHeight + 3;
                if (labelY < doc.internal.pageSize.height - 10) {
                    doc.text(`Componente ${i + 1}`, x, labelY);
                }
            }
            
        } catch (error) {
            console.error(`❌ Error adding image ${i + 1}:`, error);
            console.error(`❌ Image URL that failed: ${imageUrl}`);
            
            // Fallback: Add image placeholder
            doc.setFillColor(colors.secondary);
            doc.rect(x, y, imageWidth, imageHeight, 'F');
            
            // Add error text
            doc.setFontSize(8);
            doc.setTextColor(colors.text);
            doc.text('Error cargando', x, y + imageHeight/2);
            
            // Add image label
            const labelY = y + imageHeight + 5;
            if (labelY < doc.internal.pageSize.height - 10) {
                doc.text(`Imagen ${i + 1}`, x, labelY);
            }
        }
    }
}

/**
 * Add footer with contact information
 */
function addFooter(doc, colors) {
    const pageHeight = doc.internal.pageSize.height;
    const footerY = pageHeight - 30;
    
    // Footer background - lighter background for better contrast
    doc.setFillColor('#1A1A1A'); // Dark gray background (lighter than black)
    doc.rect(0, footerY, 210, 30, 'F');
    
    // Footer top line
    doc.setDrawColor(colors.primary);
    doc.setLineWidth(1);
    doc.line(10, footerY, 200, footerY);
    
    // Contact information section - using larger fonts and better spacing
    const contactY = footerY + 12;
    
    // Phone/WhatsApp
    doc.setTextColor('#FFFFFF'); // White text for visibility
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('PBX y Whatsapp:', 15, contactY);
    doc.setFont('helvetica', 'normal');
    doc.text('2354-2000', 60, contactY);
    
    // Email
    doc.setFont('helvetica', 'bold');
    doc.text('Email:', 105, contactY);
    doc.setFont('helvetica', 'normal');
    doc.text('ventas@nonosky.com', 125, contactY);
    
    // Website
    doc.setFont('helvetica', 'bold');
    doc.text('Web:', 15, contactY + 8);
    doc.setFont('helvetica', 'normal');
    doc.text('nonosky.com', 35, contactY + 8);
    
    // Generation info at bottom
    doc.setTextColor('#E0E0E0'); // Light gray for generation info
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Generado por Sistema NONOSKY', 15, pageHeight - 5);
    doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 150, pageHeight - 5);
}

/**
 * Load and convert images to base64 for PDF inclusion
 */
async function loadImageAsBase64(imageUrl) {
    try {
        console.log('🖼️ Loading image:', imageUrl);
        
        // Handle both absolute URLs and relative paths
        let fullUrl;
        if (imageUrl.startsWith('http')) {
            fullUrl = imageUrl;
        } else {
            // For relative paths, construct the full URL
            fullUrl = window.location.origin + imageUrl;
        }
        
        console.log('🔗 Full image URL:', fullUrl);
        
        const response = await fetch(fullUrl, {
            mode: 'cors',
            credentials: 'omit'
        });
        
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        console.log('✅ Image blob loaded:', blob.type, blob.size, 'bytes');
        
        if (blob.size === 0) {
            throw new Error('Empty image blob received');
        }
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                console.log('✅ Image converted to base64, length:', reader.result.length);
                resolve(reader.result);
            };
            reader.onerror = (error) => {
                console.error('❌ FileReader error:', error);
                reject(error);
            };
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('❌ Error loading image:', error);
        return null;
    }
}
